import { useEffect, useMemo, useState, FormEvent } from "react";
import { Link } from "react-router-dom";
import { useGoogleReCaptcha } from "@google-recaptcha/react";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { questions, type Question } from "@/questions";

type Gender = "男" | "女" | null;
type AgeRange = "18-24" | "25-34" | "35-54" | "55 歲或以上" | null;
type Answers = Record<number, number>;

function shuffleArray<T>(array: T[]): T[] {
  const arr = [...array];
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
}

const GROUP_MAX_SCORE = 36;

const groupResults: Record<
  string,
  { type: string; typeEn: string; description: string; descriptionEn: string; color: string }
> = {
  l_f: {
    type: "主導引領型",
    typeEn: "The Guiding Light",
    description: "你在愛情中傾向主動規劃與引領方向。你的決斷力與責任感讓伴侶感到安心，你習慣用行動與規劃來表達愛意。",
    descriptionEn:
      "You tend to take the lead and plan in relationships. Your decisiveness and sense of responsibility make your partner feel secure.",
    color: "#E86A33",
  },
  e_r: {
    type: "熱情表達型",
    typeEn: "The Open Heart",
    description: "你重視情感的流動與坦誠溝通。你願意分享內心世界，也期待伴侶的回應，認為愛需要被說出來、被感受到。",
    descriptionEn:
      "You value emotional flow and open communication. You share your inner world and expect your partner's response, believing love needs to be spoken and felt.",
    color: "#FF6B8A",
  },
  a_n: {
    type: "隨性自由型",
    typeEn: "The Free Spirit",
    description: "你嚮往自在與體驗，不被傳統框架束縛。對你來說，愛情是兩個獨立靈魂的相遇，一起探索人生的無限可能。",
    descriptionEn:
      "You crave freedom and experience, unbound by traditional frameworks. For you, love is two independent souls meeting to explore life's infinite possibilities.",
    color: "#4ECDC4",
  },
  g_c: {
    type: "共同成長型",
    typeEn: "The Growth Partner",
    description: "你將愛情視為共同進化的旅程。你願意與伴侶一起面對挑戰、學習新知，在磨合中讓關係變得更深刻。",
    descriptionEn:
      "You see love as a journey of mutual evolution. You're willing to face challenges and learn together, making your relationship deeper through growth.",
    color: "#9B72FF",
  },
};

const balancedResult = {
  type: "均衡包容型",
  typeEn: "The Balanced Lover",
  description: "你的愛情光環柔和而多元。你在主動與隨性、表達與沉靜、自由與承諾之間找到了自己的平衡。",
  descriptionEn:
    "Your love aura is gentle and multifaceted. You've found your own balance between initiative and spontaneity, expression and calm, freedom and commitment.",
  color: "#FFB347",
};

export default function AuraTestPage() {
  const [shuffledQuestions] = useState<Question[]>(() => shuffleArray(questions));
  const googleReCaptcha = useGoogleReCaptcha();

  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [gender, setGender] = useState<Gender>(null);
  const [ageRange, setAgeRange] = useState<AgeRange>(null);
  const [answers, setAnswers] = useState<Answers>({});
  const [currentIndex, setCurrentIndex] = useState(0);
  const [scores, setScores] = useState<Record<string, number> | null>(null);
  const [pendingAnswer, setPendingAnswer] = useState<{ questionId: number; value: number } | null>(null);

  const [email, setEmail] = useState("");
  const [emailSubmitted, setEmailSubmitted] = useState(false);
  const [emailError, setEmailError] = useState("");
  const [emailLoading, setEmailLoading] = useState(false);
  const [registerToken, setRegisterToken] = useState<string | null>(null);

  const totalSteps = 4 + shuffledQuestions.length;
  const progressPercent = Math.round(((currentIndex + 1) / totalSteps) * 100);

  const isNameStep = currentIndex === 0;
  const isPhoneStep = currentIndex === 1;
  const isGenderStep = currentIndex === 2;
  const isAgeStep = currentIndex === 3;
  const questionIndex = currentIndex - 4;
  const currentQuestion =
    questionIndex >= 0 && questionIndex < shuffledQuestions.length ? shuffledQuestions[questionIndex] : null;

  const displaySelectedValue =
    currentQuestion && pendingAnswer?.questionId === currentQuestion.id ? pendingAnswer.value : null;

  const handleSelectName = () => setCurrentIndex(1);
  const handleSelectPhone = () => setCurrentIndex(2);
  const handleSelectGender = (g: Gender) => {
    setGender(g);
    setCurrentIndex(3);
  };
  const handleSelectAge = (a: AgeRange) => {
    setAgeRange(a);
    setCurrentIndex(4);
  };
  const handleSelectAnswer = (questionId: number, value: number) => {
    const nextAnswers = { ...answers, [questionId]: value };
    setAnswers(nextAnswers);
    if (questionIndex + 1 < shuffledQuestions.length) {
      setCurrentIndex((prev) => prev + 1);
    } else {
      finishQuiz(nextAnswers);
    }
  };

  const handleSelectAnswerWithDelay = (questionId: number, value: number) => {
    if (pendingAnswer) return;

    const nextAnswers = { ...answers, [questionId]: value };
    setPendingAnswer({ questionId, value });
    setAnswers(nextAnswers);

    window.setTimeout(() => {
      setPendingAnswer(null);
      if (questionIndex + 1 < shuffledQuestions.length) {
        setCurrentIndex((prev) => prev + 1);
      } else {
        finishQuiz(nextAnswers);
      }
    }, 180);
  };

  useEffect(() => {
    setPendingAnswer(null);
  }, [currentIndex]);

  const finishQuiz = (finalAnswers: Answers) => {
    const totals: Record<string, number> = { l_f: 0, e_r: 0, a_n: 0, g_c: 0 };
    shuffledQuestions.forEach((q) => {
      const v = finalAnswers[q.id];
      if (v) totals[q.group] += v;
    });
    setScores(totals);
  };

  const result = useMemo(() => {
    if (!scores) return null;
    const entries = Object.entries(scores);
    const maxScore = Math.max(...entries.map(([, v]) => v));
    const topKeys = entries.filter(([, v]) => v === maxScore).map(([k]) => k);

    if (topKeys.length > 1) return balancedResult;
    return groupResults[topKeys[0]] ?? balancedResult;
  }, [scores]);

  const registerLink = useMemo(() => {
    if (registerToken) return `/register?t=${encodeURIComponent(registerToken)}`;
    const params = new URLSearchParams();
    if (email) params.set("email", email);
    if (name) params.set("name", name);
    if (gender) params.set("gender", gender);
    const qs = params.toString();
    return qs ? `/register?${qs}` : "/register";
  }, [email, name, gender, registerToken]);

  const handleRestart = () => {
    setName("");
    setPhone("");
    setGender(null);
    setAgeRange(null);
    setAnswers({});
    setCurrentIndex(0);
    setScores(null);
    setEmail("");
    setEmailSubmitted(false);
    setEmailError("");
    setEmailLoading(false);
    setRegisterToken(null);
  };

  const groupNames: Record<string, string> = {
    l_f: "主導力",
    e_r: "表達力",
    a_n: "自由力",
    g_c: "成長力",
  };

  const handleEmailSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setEmailError("");
    setEmailLoading(true);

    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setEmailError("Please enter a valid email address.");
      setEmailLoading(false);
      return;
    }

    const isRecaptchaEnabled = !!(import.meta.env.VITE_RECAPTCHA_SITE_KEY as string | undefined);
    let recaptchaToken = "";
    if (isRecaptchaEnabled) {
      if (!googleReCaptcha?.executeV3) {
        setEmailError("reCAPTCHA is not ready. Please try again.");
        setEmailLoading(false);
        return;
      }
      try {
        recaptchaToken = await googleReCaptcha.executeV3("lovetype");
      } catch {
        setEmailError("reCAPTCHA is not ready. Please try again.");
        setEmailLoading(false);
        return;
      }
    }

    const body = new URLSearchParams({
      email,
      resultType: result?.type || "",
      resultTypeEn: result?.typeEn || "",
      name: name || "Anonymous",
      phone,
      gender: gender || "Not specified",
      ageRange: ageRange || "Not specified",
      l_f: String(scores?.l_f ?? ""),
      e_r: String(scores?.e_r ?? ""),
      a_n: String(scores?.a_n ?? ""),
      g_c: String(scores?.g_c ?? ""),
      recaptchaToken,
    });

    try {
      const response = await fetch("/api/form/lovetype", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });

      const data = (await response.json().catch(() => null)) as {
        ok?: boolean;
        inviteToken?: string | null;
        error?: string;
      } | null;
      if (!response.ok || !data?.ok) {
        if (data?.error === "recaptcha_failed") {
          setEmailError("reCAPTCHA verification failed. Please try again.");
          return;
        }
        throw new Error("Request failed");
      }
      if (data.inviteToken) {
        setRegisterToken(data.inviteToken);
      }
      setEmailSubmitted(true);
    } catch {
      setEmailError("Something went wrong. Please try again.");
    } finally {
      setEmailLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFAF9]">
      <Navbar />

      <section className="relative pt-28 pb-20 md:pt-36 md:pb-28">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 right-0 w-[55%] h-[65%] bg-[radial-gradient(ellipse_at_top_right,_#E6D9FF_0%,_transparent_65%)] opacity-40" />
          <div className="absolute bottom-0 left-0 w-[45%] h-[50%] bg-[radial-gradient(ellipse_at_bottom_left,_#F0EBFF_0%,_transparent_70%)] opacity-40" />
        </div>

        <div className="relative z-10 max-w-2xl mx-auto px-6 md:px-10">
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-2 bg-[#E6D9FF] text-[#5B3FA0] text-xs font-semibold px-4 py-1.5 rounded-full mb-5">
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-heart-3-line"></i>
              </span>
              Love Aura Test · 戀愛頻率鑑定
            </div>
            <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3">尋找你的同頻火花</h1>
            <p className="text-gray-500 text-sm md:text-base">The Pulse of Finding Your Sync</p>
          </div>

          {!scores ? (
            <div className="bg-white rounded-3xl p-8 md:p-10 border border-gray-100">
              {/* Progress */}
              <div className="mb-8">
                <div className="flex items-center justify-between text-xs text-gray-400 mb-2">
                  <span>Progress</span>
                  <span>{progressPercent}%</span>
                </div>
                <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-[#9B72FF] rounded-full transition-all duration-300"
                    style={{ width: `${progressPercent}%` }}
                  />
                </div>
              </div>

              {/* Question Card */}
              <div className="min-h-[260px]">
                {isNameStep ? (
                  <div className="flex flex-col gap-6">
                    <div>
                      <h2 className="text-lg font-semibold text-gray-900 mb-1">你的名字（可選）</h2>
                      <p className="text-sm text-gray-400">Your Name (Optional)</p>
                    </div>
                    <input
                      type="text"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="輸入名字 / Enter your name"
                      className="w-full border border-gray-200 rounded-xl px-4 py-3 text-base md:text-sm text-gray-900 placeholder-gray-300 focus:outline-none focus:border-[#9B72FF] transition-colors"
                    />
                    <p className="text-xs text-gray-400">可留空，直接下一題 / Optional, skip to next</p>
                    <button
                      onClick={handleSelectName}
                      className="self-start inline-flex items-center gap-2 bg-gray-900 text-white text-sm font-semibold px-6 py-3 rounded-full hover:bg-gray-700 transition-all duration-200 cursor-pointer whitespace-nowrap"
                    >
                      Next
                      <span className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-arrow-right-line"></i>
                      </span>
                    </button>
                  </div>
                ) : isPhoneStep ? (
                  <div className="flex flex-col gap-6">
                    <div>
                      <h2 className="text-lg font-semibold text-gray-900 mb-1">你的聯絡電話（可選）</h2>
                      <p className="text-sm text-gray-400">Phone Number (Optional)</p>
                    </div>
                    <p className="text-xs text-gray-400">
                      If you want 2 Months Free Premium Subscription to the Aurays App to meet intentional singles and
                      build genuine connections, leave your number!
                      <br />
                      想免費獲得 Aurays App 2個月進階版會籍，結識認真的對象並建立真實連繫嗎？請留下你的聯絡電話！
                    </p>
                    <input
                      type="tel"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      placeholder="WhatsApp / Phone"
                      className="w-full border border-gray-200 rounded-xl px-4 py-3 text-base md:text-sm text-gray-900 placeholder-gray-300 focus:outline-none focus:border-[#9B72FF] transition-colors"
                    />
                    <p className="text-xs text-gray-400">可留空，直接下一題 / Optional, skip to next</p>
                    <button
                      onClick={handleSelectPhone}
                      className="self-start inline-flex items-center gap-2 bg-gray-900 text-white text-sm font-semibold px-6 py-3 rounded-full hover:bg-gray-700 transition-all duration-200 cursor-pointer whitespace-nowrap"
                    >
                      Next
                      <span className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-arrow-right-line"></i>
                      </span>
                    </button>
                  </div>
                ) : isGenderStep ? (
                  <div className="flex flex-col gap-6">
                    <div>
                      <h2 className="text-lg font-semibold text-gray-900 mb-1">你的性別</h2>
                      <p className="text-sm text-gray-400">Your Gender</p>
                    </div>
                    <div className="flex flex-wrap gap-3">
                      {(["男", "女"] as const).map((g) => (
                        <button
                          key={g}
                          onClick={() => handleSelectGender(g)}
                          className={`px-5 py-3 rounded-full text-sm font-medium border transition-all duration-200 cursor-pointer whitespace-nowrap ${
                            gender === g
                              ? "bg-[#9B72FF] text-white border-[#9B72FF]"
                              : "bg-white text-gray-700 border-gray-200 md:hover:border-[#9B72FF]"
                          }`}
                        >
                          {g === "男" ? "男 Male" : "女 Female"}
                        </button>
                      ))}
                      <button
                        onClick={() => handleSelectGender(null)}
                        className={`px-5 py-3 rounded-full text-sm font-medium border transition-all duration-200 cursor-pointer whitespace-nowrap ${
                          gender === null
                            ? "bg-[#9B72FF] text-white border-[#9B72FF]"
                            : "bg-white text-gray-700 border-gray-200 md:hover:border-[#9B72FF]"
                        }`}
                      >
                        不便透露 / Prefer not to say
                      </button>
                    </div>
                    <p className="text-xs text-gray-400">
                      請選擇其一，或選擇不透露 / Please select one, or prefer not to say
                    </p>
                  </div>
                ) : isAgeStep ? (
                  <div className="flex flex-col gap-6">
                    <div>
                      <h2 className="text-lg font-semibold text-gray-900 mb-1">你的年齡</h2>
                      <p className="text-sm text-gray-400">Your Age</p>
                    </div>
                    <div className="flex flex-col gap-3 items-start">
                      {(["18-24", "25-34", "35-54", "55 歲或以上"] as const).map((r) => (
                        <button
                          key={r}
                          onClick={() => handleSelectAge(r)}
                          className={`px-5 py-3 rounded-full text-sm font-medium border transition-all duration-200 cursor-pointer whitespace-nowrap ${
                            ageRange === r
                              ? "bg-[#9B72FF] text-white border-[#9B72FF]"
                              : "bg-white text-gray-700 border-gray-200 md:hover:border-[#9B72FF]"
                          }`}
                        >
                          {r === "55 歲或以上" ? "55 歲或以上 / 55 or above" : r}
                        </button>
                      ))}
                    </div>
                  </div>
                ) : currentQuestion ? (
                  <div className="min-h-[360px] md:min-h-[280px] flex flex-col gap-6">
                    <div className="flex-1">
                      <h2 className="text-lg font-semibold text-gray-900 mb-1">{currentQuestion.text}</h2>
                      <p className="text-sm text-gray-400">{currentQuestion.englishText}</p>
                    </div>

                    <div className="flex items-center justify-between text-xs text-gray-400">
                      <div className="text-left">
                        <p>非常不同意</p>
                        <p>Strongly Disagree</p>
                      </div>
                      <div className="text-right">
                        <p>非常同意</p>
                        <p>Strongly Agree</p>
                      </div>
                    </div>

                    <div className="flex justify-between gap-2">
                      {[1, 2, 3, 4, 5, 6].map((v) => (
                        <button
                          key={`${currentQuestion.id}-${v}`}
                          type="button"
                          onClick={(e) => {
                            e.currentTarget.blur();
                            handleSelectAnswerWithDelay(currentQuestion.id, v);
                          }}
                          disabled={!!pendingAnswer}
                          className={`w-10 h-10 md:w-12 md:h-12 rounded-full flex items-center justify-center text-sm font-semibold border transition-all duration-200 cursor-pointer ${
                            displaySelectedValue === v
                              ? "bg-[#9B72FF] text-white border-[#9B72FF] scale-110"
                              : "bg-white text-gray-700 border-gray-200 md:hover:border-[#9B72FF] md:hover:text-[#9B72FF]"
                          }`}
                        >
                          {v}
                        </button>
                      ))}
                    </div>
                  </div>
                ) : null}
              </div>
            </div>
          ) : scores && !emailSubmitted ? (
            <div className="bg-white rounded-3xl p-8 md:p-10 border border-gray-100 text-center">
              <div className="w-16 h-16 mx-auto rounded-full flex items-center justify-center mb-5 bg-[#E6D9FF]">
                <span className="w-8 h-8 flex items-center justify-center">
                  <i className="ri-mail-send-line text-2xl text-[#9B72FF]"></i>
                </span>
              </div>

              <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-2">我們正在分析你的戀愛人格/頻率</h2>
              <p className="text-sm text-gray-500 mb-2">We&apos;ve captured your result.</p>
              <p className="text-sm text-gray-600 leading-relaxed mb-8 max-w-sm mx-auto">
                請輸入 Email，我哋會把你的專屬報告即時發送到你的收件箱。
                <br />
                To receive your detailed analysis, please enter your email below.
              </p>

              <form
                id="aura-test-email-form"
                data-readdy-form
                onSubmit={handleEmailSubmit}
                className="flex flex-col gap-4 max-w-sm mx-auto"
              >
                <input
                  type="email"
                  name="email"
                  required
                  placeholder="your@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full border border-gray-200 rounded-xl px-4 py-3 text-base md:text-sm text-gray-900 placeholder-gray-300 focus:outline-none focus:border-[#9B72FF] transition-colors"
                />
                {emailError && <p className="text-sm text-red-500 bg-red-50 px-4 py-2 rounded-lg">{emailError}</p>}
                <button
                  type="submit"
                  disabled={emailLoading}
                  className={`w-full bg-[#9B72FF] text-white text-sm font-semibold py-3.5 rounded-full transition-all duration-200 whitespace-nowrap ${
                    emailLoading ? "opacity-60 cursor-not-allowed" : "hover:bg-[#7C55CC] cursor-pointer"
                  }`}
                >
                  {emailLoading ? "Sending..." : "Send My Report"}
                </button>
              </form>
            </div>
          ) : emailSubmitted ? (
            <div className="bg-white rounded-3xl p-8 md:p-10 border border-gray-100 text-center">
              <div className="w-16 h-16 mx-auto rounded-full flex items-center justify-center mb-5 bg-[#E6D9FF]">
                <span className="w-8 h-8 flex items-center justify-center">
                  <i className="ri-check-line text-2xl text-[#9B72FF]"></i>
                </span>
              </div>

              <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-2">專屬報告已發送！</h2>
              <p className="text-sm text-gray-500 mb-2">Your report is on its way!</p>
              <p className="text-sm text-gray-600 leading-relaxed mb-8 max-w-sm mx-auto">
                想遇見同頻的人？
                <br />
                登記成為Aurays【種子會員】
                <br />
                優先開啟專屬配對。
                <br />
                Ready to meet your sync?
                <br />
                Join Aurays for priority matching.
              </p>

              <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                <button
                  onClick={handleRestart}
                  className="inline-flex items-center gap-2 bg-gray-100 text-gray-700 text-sm font-semibold px-6 py-3 rounded-full hover:bg-gray-200 transition-all duration-200 cursor-pointer whitespace-nowrap"
                >
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-refresh-line"></i>
                  </span>
                  Retake Test
                </button>
                <Link
                  to={registerLink}
                  className="inline-flex items-center gap-2 bg-[#9B72FF] text-white text-sm font-semibold px-6 py-3 rounded-full hover:bg-[#7C55CC] transition-all duration-200 cursor-pointer whitespace-nowrap"
                >
                  Join Aurays
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-arrow-right-line"></i>
                  </span>
                </Link>
              </div>
            </div>
          ) : null}
        </div>
      </section>

      <Footer />
    </div>
  );
}
